/**
 * Pure mappers for the Account redesign: KYC status -> banner/pill, completeness
 * ring %, tier labels, and value formatters. Kept free of React so they are easy
 * to unit-test and reuse.
 *
 * Decisions baked in (confirmed with product):
 *  - Completeness % uses Tier + `KYC.validated` bands (not per-field weighting).
 *  - Banner/pill is driven by `KYC.validated` + `stats.tier`, with `stats.status`
 *    only used to distinguish "returned" vs "declined".
 */
import * as ROUTES from "@/constants/route";

export type StatusKind = "approved" | "review" | "action";

export interface AccountStatus {
  kind: StatusKind;
  /** Short label for the left-panel status pill. */
  pill: string;
  /** Banner heading. */
  title: string;
  /** Banner supporting note. */
  note: string;
  /** Optional CTA shown in the banner. */
  ctaLabel?: string;
  ctaTo?: string;
}

const RETURNED = new Set(["SHORT_KYC-3", "LONG_KYC-3"]);
const DECLINED = new Set(["SHORT_KYC-4", "LONG_KYC-4"]);

function reviewDetailsRoute(status: string | undefined, tier: unknown): string {
  if (status?.startsWith("LONG_KYC-") || normalizeTier(tier) === 3) {
    return ROUTES.DECLINEDUPGRADE_PAGE;
  }
  return ROUTES.DECLINED_PAGE;
}

export function normalizeTier(tier: unknown): 1 | 2 | 3 {
  const n = Number(tier);
  if (n === 2) return 2;
  if (n === 3) return 3;
  return 1;
}

/**
 * Completeness bands. Verified Tier 3 = 100%. Submitted-but-pending nudges the
 * user toward the next band so the ring visibly moves after submitting KYC.
 */
export function computeCompleteness(
  tier: unknown,
  validated: string | undefined
): number {
  const t = normalizeTier(tier);
  if (t === 3) return validated === "verified" ? 100 : 90;
  if (t === 2) return validated === "pending" ? 83 : 66;
  // Tier 1
  return validated === "pending" ? 50 : 33;
}

export function tierLabel(tier: unknown): string {
  switch (normalizeTier(tier)) {
    case 3:
      return "Tier 3 \u00b7 Premier";
    case 2:
      return "Tier 2 \u00b7 Account Verified";
    default:
      return "Tier 1 \u00b7 Identity Verified";
  }
}

export function getAccountStatus(
  validated: string | undefined,
  tier: unknown,
  status: string | undefined
): AccountStatus {
  if (validated === "verified") {
    return {
      kind: "approved",
      pill: "Approved",
      title: "Your account is active.",
      note: "You\u2019re all set to start investing \u2014 explore funds right from your dashboard.",
      ctaLabel: "Go to dashboard",
      ctaTo: ROUTES.DASHBOARD,
    };
  }

  if (validated === "declined" || (status && DECLINED.has(status))) {
    return {
      kind: "action",
      pill: "Action needed",
      title: "Action needed on your account.",
      note: "Your application was declined. Please review our feedback and resubmit.",
      ctaLabel: "Review details",
      ctaTo: reviewDetailsRoute(status, tier),
    };
  }

  if (status && RETURNED.has(status)) {
    return {
      kind: "action",
      pill: "Action needed",
      title: "Action needed on your account.",
      note: "Some details need your attention before we can finish reviewing your application.",
      ctaLabel: "Review details",
      ctaTo: reviewDetailsRoute(status, tier),
    };
  }

  if (validated === "pending") {
    return {
      kind: "review",
      pill: "Under review",
      title: "Your account is under review.",
      note: "We\u2019re processing your Client Application \u2014 you\u2019ll be notified once it\u2019s approved.",
    };
  }

  // notValidated / Tier 1 with no submitted application.
  return {
    kind: "action",
    pill: "Action needed",
    title: "Complete your application.",
    note: "Finish your Client Application to verify your account and start investing.",
    ctaLabel: "Complete application",
    ctaTo: ROUTES.LONG_KYC,
  };
}

/** Annual gross income code (1..7) -> label. Mirrors MyApplication.js. */
export function decodeAnnualIncome(code: unknown): string {
  switch (Number(code)) {
    case 1:
      return "PHP 50,000 or less";
    case 2:
      return "PHP 50,001 to PHP 100,000";
    case 3:
      return "PHP 100,001 to PHP 500,000";
    case 4:
      return "PHP 500,001 to PHP 1,000,000";
    case 5:
      return "PHP 1,000,001 to PHP 5,000,000";
    case 6:
      return "PHP 5,000,001 to PHP 10,000,000";
    case 7:
      return "PHP 10,000,001 or more";
    default:
      return "\u2014";
  }
}

/** Mask all but the last 4 digits of a settlement account number. */
export function maskAccountNumber(value: unknown): string {
  const raw = String(value ?? "").replace(/\s+/g, "");
  if (!raw || raw === "N/A") return "\u2014";
  if (raw.length <= 4) return raw;
  const last4 = raw.slice(-4);
  return `\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 ${last4}`;
}

export function getInitials(firstName?: string, lastName?: string): string {
  const f = (firstName || "").trim();
  const l = (lastName || "").trim();
  const initials = `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
  return initials || "\u2014";
}

export function displayValue(value: unknown): string {
  if (value === null || value === undefined) return "\u2014";
  if (Array.isArray(value)) {
    return (
      value
        .map((item) => String(item ?? "").trim())
        .filter(Boolean)
        .join(", ") || "—"
    );
  }
  const s = String(value).trim();
  return s === "" || s === "N/A" ? "\u2014" : s;
}
